import { Box, Paper, useMediaQuery, useTheme, Drawer, IconButton, Slide } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useTranslate, useStore } from 'react-admin';
import { useCallback, useEffect, useRef } from 'react';
import { useConversationsPage } from './hooks/useConversationsPage';
import { ConversationListPanel } from './components/ConversationListPanel';
import { ChatPanelContainer } from './components/ChatPanel';
import { DetailsPanel } from './components/DetailsPanel';
import { ConnectionStatusBanner } from '../../components/chat/ConnectionStatusBanner';
import { EmptyConversationState } from '../../components/chat/EmptyConversationState';
import type { Conversation } from '../../types/chat.types';

/**
 * ConversationsPage - Conversation-centric interface for viewing all conversations
 *
 * Responsive Breakpoints:
 * - Mobile (<900px): Single panel view with slide navigation
 * - Tablet/Notebook (900-1200px): 2-column layout (list + chat), drawer for details
 * - Desktop (≥1200px): 3-column layout with inline details panel
 *
 * Layout widths:
 * - Conversation list: 280px (tablet) / 320px (desktop) / 360px (large desktop)
 * - Details panel: 320px (inline when open on desktop)
 * - Chat panel: flex (fills remaining space)
 */

// Layout constants
const LAYOUT = {
  listWidth: {
    tablet: 280,
    desktop: 320,
    largeDesktop: 360,
  },
  detailsWidth: 320,
  headerHeight: 48,
  mobileHeaderHeight: 56,
};

export function ConversationsPage() {
  const theme = useTheme();
  const translate = useTranslate();

  // Breakpoints
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // < 900px
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg')); // 900-1200px
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg')); // >= 1200px
  const isLargeDesktop = useMediaQuery(theme.breakpoints.up('xl')); // >= 1440px

  const [sidebarOpen, setSidebarOpen] = useStore('sidebar.open', true);
  const previousSidebarState = useRef(sidebarOpen);

  // Collapse platform sidebar on mount, restore on unmount
  useEffect(() => {
    previousSidebarState.current = sidebarOpen;
    setSidebarOpen(false);
    return () => {
      setSidebarOpen(previousSidebarState.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    selectedConversationId,
    selectedConversation,
    isLoadingConversation,
    isDetailsPanelOpen,
    selectConversation,
    toggleDetailsPanel,
    closeDetailsPanel,
  } = useConversationsPage();

  const handleConversationSelect = (conversation: Conversation) => {
    selectConversation(Number(conversation.id));
  };

  const handleBackToList = () => {
    selectConversation(null);
  };

  const handleForwardSuccess = useCallback((conversationId: number) => {
    selectConversation(conversationId);
  }, [selectConversation]);

  // Determine list width based on breakpoint
  const listWidth = isLargeDesktop
    ? LAYOUT.listWidth.largeDesktop
    : isDesktop
      ? LAYOUT.listWidth.desktop
      : LAYOUT.listWidth.tablet;

  // Mobile layout: Single panel with slide transitions
  if (isMobile) {
    const showChat = !!selectedConversationId;

    return (
      <Box
        sx={{
          height: `calc(100vh - ${LAYOUT.mobileHeaderHeight}px)`,
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <ConnectionStatusBanner position="top" />

        {/* Conversation List View */}
        <Slide direction="right" in={!showChat} mountOnEnter unmountOnExit>
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'background.paper',
              zIndex: showChat ? 0 : 1,
            }}
          >
            <ConversationListPanel
              selectedConversationId={selectedConversationId}
              onConversationSelect={(conv) => {
                handleConversationSelect(conv);
              }}
              width="100%"
            />
          </Box>
        </Slide>

        {/* Chat View */}
        <Slide direction="left" in={showChat} mountOnEnter unmountOnExit>
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'background.paper',
              zIndex: showChat ? 1 : 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Back button */}
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                left: 8,
                zIndex: 10,
              }}
            >
              <IconButton
                onClick={handleBackToList}
                sx={{
                  bgcolor: 'background.paper',
                  boxShadow: 1,
                  '&:hover': { bgcolor: 'background.paper' },
                }}
              >
                <ArrowBackIcon />
              </IconButton>
            </Box>

            <ChatPanelContainer
              conversation={selectedConversation}
              isLoading={isLoadingConversation}
              onToggleDetails={toggleDetailsPanel}
              isDetailsOpen={isDetailsPanelOpen}
              onForwardSuccess={handleForwardSuccess}
            />
          </Box>
        </Slide>

        {/* Details Drawer */}
        <Drawer
          anchor="right"
          open={isDetailsPanelOpen && !!selectedConversation}
          onClose={closeDetailsPanel}
          sx={{
            '& .MuiDrawer-paper': {
              width: '90%',
              maxWidth: 360,
            },
          }}
        >
          <DetailsPanel
            conversation={selectedConversation}
            onClose={closeDetailsPanel}
            showCloseButton={true}
            variant="drawer"
            context="conversation"
          />
        </Drawer>
      </Box>
    );
  }

  // Tablet layout: 2 columns (list + chat), drawer for details
  if (isTablet) {
    return (
      <Box
        sx={{
          height: `calc(100vh - ${LAYOUT.headerHeight}px)`,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <ConnectionStatusBanner position="top" />

        <Paper
          sx={{
            display: 'flex',
            flex: 1,
            minHeight: 0,
            borderRadius: 0,
          }}
        >
          {/* Left Panel: Conversation List - Fixed width */}
          <ConversationListPanel
            selectedConversationId={selectedConversationId}
            onConversationSelect={handleConversationSelect}
            width={listWidth}
          />

          {/* Center Panel: Chat - Flex */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              height: '100%',
              flexDirection: 'column',
              minWidth: 0,
              borderLeft: '1px solid',
              borderColor: 'divider',
            }}
          >
            {selectedConversationId ? (
              <ChatPanelContainer
                conversation={selectedConversation}
                isLoading={isLoadingConversation}
                onToggleDetails={toggleDetailsPanel}
                isDetailsOpen={isDetailsPanelOpen}
                onForwardSuccess={handleForwardSuccess}
              />
            ) : (
              <EmptyConversationState
                title={translate('resources.chat.noConversationSelected')}
                subtitle={translate('resources.chat.selectConversationHint')}
              />
            )}
          </Box>
        </Paper>

        {/* Details Drawer */}
        <Drawer
          anchor="right"
          open={isDetailsPanelOpen && !!selectedConversation}
          onClose={closeDetailsPanel}
          sx={{
            '& .MuiDrawer-paper': {
              width: 360,
              maxWidth: '90vw',
            },
          }}
        >
          <DetailsPanel
            conversation={selectedConversation}
            onClose={closeDetailsPanel}
            showCloseButton={true}
            variant="drawer"
            context="conversation"
          />
        </Drawer>
      </Box>
    );
  }

  // Desktop layout: 3 columns with inline details
  return (
    <Box
      sx={{
        height: `calc(100vh - ${LAYOUT.headerHeight}px)`,
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <ConnectionStatusBanner position="top" />

      <Paper
        sx={{
          display: 'flex',
          flex: 1,
          minHeight: 0,
        }}
      >
        {/* Left Panel: Conversation List */}
        <ConversationListPanel
          selectedConversationId={selectedConversationId}
          onConversationSelect={handleConversationSelect}
          width={listWidth}
        />

        {/* Center Panel: Chat */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            height: '100%',
            flexDirection: 'column',
            minWidth: 0,
            borderLeft: '1px solid',
            borderColor: 'divider',
          }}
        >
          {selectedConversationId ? (
            <ChatPanelContainer
              conversation={selectedConversation}
              isLoading={isLoadingConversation}
              onToggleDetails={toggleDetailsPanel}
              isDetailsOpen={isDetailsPanelOpen}
              onForwardSuccess={handleForwardSuccess}
            />
          ) : (
            <EmptyConversationState
              title={translate('resources.chat.noConversationSelected')}
              subtitle={translate('resources.chat.selectConversationHint')}
            />
          )}
        </Box>

        {/* Right Panel: Details - Inline on desktop when open */}
        {isDetailsPanelOpen && selectedConversation && (
          <Box
            sx={{
              width: LAYOUT.detailsWidth,
              flexShrink: 0,
              borderLeft: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              height: '100%',
            }}
          >
            <DetailsPanel
              conversation={selectedConversation}
              onClose={closeDetailsPanel}
              showCloseButton={true}
              variant="inline"
              width={LAYOUT.detailsWidth}
              context="conversation"
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
}

export default ConversationsPage;
